import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import {
  Link as SitecoreLink,
  LinkField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { classnames } from 'tailwindcss-classnames';
import useItemId from 'lib/sitecore/use-item-id';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import useScreenWidth from 'lib/util/useScreenWidth';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { Routes } from '.generated/templates/Project.Routes.model';
import Router from 'next/router';
import { menuLocation, pageNavigationDataLayerPush } from 'lib/util/gtm-datalayer';

/**
 * A side navigation item as rendered by sitecore
 */
interface SideNavigationItem {
  Name?: string;
  Path?: LinkField;
  Id: string;
  Children?: SideNavigationItem[];
}

/**
 * Side navigation component properties.
 */
export interface SideNavigationProps {
  fields?: {
    sidenavitemlist: SideNavigationItem[];
  };
}

/**
 * Accordian sub-component properties.
 */
interface AccordionNavProps {
  data: SideNavigationItem[];
}

/**
 *  Gets the parent for a given item.
 * @param items Items to process.
 * @param childItem Child item to find.
 * @returns The parent item.
 */
const getParentItem = (items: SideNavigationItem[], childItem: SideNavigationItem) => {
  let parentItem = null;
  items.some((item): boolean => {
    if (item?.Children && item.Children.includes(childItem)) {
      parentItem = item;
      return true; // Exit the loop once a parent is found.
    }

    if (item?.Children && item.Children.length > 0) {
      parentItem = getParentItem(item.Children, childItem);
      return parentItem !== null; // Exit the loop if a parent is found in the recursive call.
    }

    return false; // Continue searching.
  });
  return parentItem;
};

/**
 *  Gets an array of parent paths for the given item.
 * @param item Item to find parent paths for.
 * @returns An array identifiying parents by path.
 */
const getParentPaths = (items: SideNavigationItem[], item: SideNavigationItem | null) => {
  const parentPaths = [];
  let currentItem = item; // Create a separate variable

  while (currentItem) {
    if (currentItem?.Path && currentItem?.Path?.value) {
      parentPaths?.unshift(currentItem?.Path?.value?.href);
    }
    currentItem = getParentItem(items, currentItem); // Update the separate variable
  }
  return parentPaths;
};

/**
 *  Finds a Side Navigation item by id
 * @param items Items to process
 * @param itemId Id of the item to find
 * @returns The parent item
 */
const findItemToOpen = (
  items: SideNavigationItem[],
  itemId?: string
): SideNavigationItem | null => {
  let result = null;

  items.some((item: SideNavigationItem) => {
    if (item?.Id && item.Id.toLocaleLowerCase() === itemId) {
      result = item;
      return true; // Exit the loop once the item is found.
    }

    if (item?.Children && item.Children.length > 0) {
      result = findItemToOpen(item.Children, itemId);
      return result !== null; // Exit the loop if the item is found in the recursive call.
    }
    return false; // Continue searching.
  });
  return result;
};

/**
 * Properties passed to updateAccordianNavSelectedItem
 */
type UpdateAccordianNavSelectedItemProps = {
  data: SideNavigationItem[];
  itemId?: string;
  setOpenItems: Dispatch<SetStateAction<string[]>>;
};

/**
 *  Gets the parent for a given item
 * @param props An UpdateAccordianNavSelectedItemProps containing nav data, item id, and state updator
 * @param childItem Child item to find
 */
const updateAccordianNavSelectedItem = ({
  data,
  itemId,
  setOpenItems,
}: UpdateAccordianNavSelectedItemProps) => {
  // Find the corresponding item in the navigation
  const itemToOpen: SideNavigationItem | null = findItemToOpen(data, itemId);
  // Open the corresponding item by its Path
  if (itemToOpen) {
    setOpenItems(getParentPaths(data, itemToOpen) as string[]);
  }
};
const MobileNav = ({ data }: AccordionNavProps) => {
  const itemId = useItemId()?.toLocaleLowerCase();
  const [activeItem, setActiveItem] = useState<SideNavigationItem>(
    data.find((x) => x.Id.toLocaleLowerCase() === itemId) as SideNavigationItem
  );
  const [isOpen, setIsOpen] = useState(false);
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };
  const handleOptionClick = (option: SideNavigationItem) => {
    setActiveItem(option);
    setIsOpen(false);
    Router.push(option.Path?.value.href as string);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
    }
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLLIElement>,
    option: SideNavigationItem
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOptionClick(option);
    }
  };
  const inputCss = classnames(
    'mt-2',
    'w-3/4',
    'p-2',
    'px-2',
    'leading-6',
    'h-10',
    'rounded-sm',
    'border',
    'border-[#A9A9A9]',
    'bg-white',
    'flex',
    'items-center',
    'justify-between'
  );
  const dropDownItem = classnames(
    'w-3/4',
    'px-2',
    'leading-6',
    'rounded-sm',
    'bg-white',
    'border',
    'border-[#A9A9A9]'
  );
  const inputSpacingCss = classnames('pb-6', 'max-w-full', 'md:max-w-[395px]');
  return (
    <div className={inputSpacingCss}>
      <div
        className={inputCss}
        role="button"
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {activeItem?.Name}{' '}
        <span className="ml-2">
          {isOpen ? (
            <svg
              className={classnames('w-5', 'h-5')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          ) : (
            <svg
              className={classnames('w-5', 'h-5')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </span>
      </div>
      {isOpen && (
        <ul className={dropDownItem} role="menu">
          {data.map((option) => (
            <li
              key={option.Id}
              role="menuitem"
              tabIndex={0}
              onClick={() => handleOptionClick(option)}
              onKeyDown={(event) => handleOptionKeyDown(event, option)}
              className={`${option.Id.toLocaleLowerCase() === itemId ? 'bg-gray-whisper' : ''} p-1`}
            >
              {option.Name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
const AccordionNav = ({ data }: AccordionNavProps) => {
  const itemId = useItemId()?.toLocaleLowerCase();
  const [openItems, setOpenItems] = useState<string[]>([]);
  useEffect(
    () => updateAccordianNavSelectedItem({ data, itemId, setOpenItems }),
    [data, itemId, setOpenItems]
  );

  const isEE = useExperienceEditor();

  const itemStyle = classnames(
    'py-[6px]',
    'pl-2',
    'hover:bg-gray-whisper',
    'hover:underline',
    'flex',
    'gap-2',
    'w-full',
    'text-base',
    'leading-2'
  );

  const renderNavItem = (item: SideNavigationItem) => {
    const isActive = item?.Id?.toLocaleLowerCase() === itemId;

    const linkClasses = classnames(`${itemStyle}`, {
      'font-bold': isActive,
      'text-body': isActive,
      'bg-blue-navy': isActive,
      'hover:bg-blue-navy': isActive,
      'hover:no-underline': isActive,
      'text-white': isActive,
      'text-solidBlack': !isActive,
    });
    const linkProps = {
      className: linkClasses,
      children: item?.Name,
    };

    if (!item?.Path?.value) {
      return <span>{item?.Name}</span>;
    }

    return (
      <div
        key={item?.Path?.value?.href}
        className={classnames('flex', 'flex-col', 'gap-1', 'py-[6px]')}
      >
        {isEE && item.Path.value ? (
          <span {...linkProps} />
        ) : (
          <SitecoreLink
            field={item.Path.value}
            {...linkProps}
            onClick={() => {
              const displayText = item.Name ? item.Name : 'undefined';
              pageNavigationDataLayerPush(displayText, menuLocation[3]);
            }}
          />
        )}
        {item?.Children && (
          <ul
            style={{
              display: openItems.includes(item?.Path?.value?.href || '') ? 'block' : 'none',
            }}
          >
            {item?.Children?.map((child) => (
              <li key={child?.Path?.value?.href} className={classnames('py-[0px]', 'pl-2')}>
                {renderNavItem(child)}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };
  return (
    <nav>
      <ul className={classnames('border-t', 'border-solid', 'border-gray-midGray')}>
        {data?.map((item: SideNavigationItem) =>
          item?.Name && item?.Path !== null ? (
            <li
              key={item?.Path?.value?.href}
              className={classnames('border-b', 'border-solid', 'border-gray-midGray', 'text-body')}
            >
              {renderNavItem(item)}
            </li>
          ) : (
            <></>
          )
        )}
      </ul>
    </nav>
  );
};

const SideNavigation = ({ fields }: SideNavigationProps) => {
  const screenWidth = useScreenWidth();
  const { sitecoreContext } = useSitecoreContext();
  const basePageData = sitecoreContext.route as Routes.GenericPage;
  const showMobilenav = basePageData.fields?.showMobileSubNavigation.value as boolean;
  if (!fields) {
    return <MissingDataSource />;
  }
  const itemsToRender: SideNavigationItem[] | undefined =
    fields?.sidenavitemlist && fields.sidenavitemlist.length > 0
      ? fields.sidenavitemlist[0]?.Children
      : undefined;
  return (
    <>
      {screenWidth > 767 ? (
        <div data-component="authorable/navigation/SideNavigation">
          <AccordionNav data={itemsToRender || []} />
        </div>
      ) : (
        showMobilenav && (
          <div data-component="authorable/navigation/SideNavigation">
            <MobileNav data={itemsToRender || []} />
          </div>
        )
      )}
    </>
  );
};
export default SideNavigation;
